'use client';

import { sendGAEvent } from '@next/third-parties/google';
import { useEffect, useRef } from 'react';
import { useStore } from '@/app/providers/store';
import { PlanDialogScene } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { getTodayDate, useDailySecondTaskAlert } from './use-alert-pricing';
import { useGlobalModal } from './use-global-modal';

const LOW_CREDITS_ALERT_KEY = 'VIDU_LOW_CREDITS_ALERT';

/** 免费用户「积分不足」提醒的积分阈值，低于该值触发弹窗 */
export const LOW_CREDITS_THRESHOLD = 20;

/**
 * 消费「进入创作 tab 低积分」弹窗当日名额：当日尚未弹过则标记为已弹并返回 true，否则返回 false。
 * 仅供 useLowCreditsAlert 使用。
 */
export const consumeLowCreditsAlertToday = (): boolean => {
  const today = getTodayDate();
  if (safeStorage.getItem(LOW_CREDITS_ALERT_KEY) === today) return false;
  safeStorage.setItem(LOW_CREDITS_ALERT_KEY, today);
  return true;
};

/**
 * 免费用户进入创作 tab（切换路由）那一刻若积分不足 20，每自然日弹一次订阅弹窗。
 * 仅以「路由切换」为触发时机：同一路由上 creditsInfo 变化（如提交扣分）不再重新判定，
 * 但切换到另一个创作工具路由时会再次判定。创作过程中的积分不足由提交路径的
 * InsufficientCredits 弹窗覆盖。
 * @param pathname 当前路径（来自 usePathname），用于区分路由切换 vs 同路由内状态变化
 * @param enabled 是否启用（默认 true）；工具聚合页等非创作页应传 false 以跳过弹窗
 */
export const useLowCreditsAlert = (enabled = true) => {
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const userInfo = useStore((state) => state.userInfo);
  const creditsInfo = useStore((state) => state.creditsInfo);
  const pathname = usePathname();
  const evaluatedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (evaluatedPathRef.current === pathname) return;
    const isFree = userInfo?.subs_plan === 'free';
    if (!isFree || !creditsInfo) return;
    evaluatedPathRef.current = pathname;
    if (creditsInfo.total >= LOW_CREDITS_THRESHOLD) return;

    setTimeout(() => {
      if (consumeLowCreditsAlertToday()) {
        openPlanDetailDialog({ scene: PlanDialogScene.LowCredits, fromPage: 'low_credits' });
        reportEvent('vip_price_modal_exposure', { from_page: 'low_credits' });
        sendGAEvent('event', 'subscription_exposure', { from_page: 'low_credits' });
      }
    }, 0);
  }, [enabled, pathname, userInfo, creditsInfo, openPlanDetailDialog]);
};

/**
 * 免费用户「当日第二次发送任务」提醒的统一处理：封装弹窗场景选择与曝光埋点。
 * 供各创作提交路径（传统创作 useTaskSubmit、工具页 useToolSubmit）复用，保证口径与埋点一致。
 * 返回 recordTask：每次发送任务成功后调用；当日第二次发送时始终弹「当日第二次发送」弹窗
 * （与积分多少无关）。创作过程中的积分不足由提交路径的 InsufficientCredits 阻断弹窗负责，
 * 「积分快用完了」(LowCredits) 仅由进入创作 tab 的 useLowCreditsAlert 触发，两条路径不交叉。
 */
export const useDailySecondTaskAlertHandler = () => {
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const userInfo = useStore((state) => state.userInfo);

  return useDailySecondTaskAlert(() => {
    if (userInfo?.subs_plan !== 'free') return;

    openPlanDetailDialog({ scene: PlanDialogScene.DailySecondTask, fromPage: 'second_task_of_day' });
    reportEvent('vip_price_modal_exposure', { from_page: 'second_task_of_day' });
    sendGAEvent('event', 'subscription_exposure', { from_page: 'second_task_of_day' });
  });
};
